import React, { FunctionComponent, useMemo, useState } from "react";
import SelectControl from "../SelectControl/SelectControl";
import BuildingsOptions from "../MultiBuildingsOptions/MultiBuildingsOptions";
import { FROM, PLACEHOLDER_SELECT_DEST, PLACEHOLDER_SELECT_START, TO } from "../../../../store/constants";
import { IBuilding } from "../../../../types/IBuilding";
import { IQueryParams } from "../../../../types/IQueryParams";
import { ILanguage } from "../../../../types/ILanguage";
import SwitchControl from "../SwitchControl/SwitchControl";
import { QueryParams } from "../../../../types/QueryParams";
import { queryParamsStore } from "../../../../store/QueryParamsStore/QueryParamsStore";
import * as _ from "lodash";
import TranslatedText from "../../../ui/TranslatedText/TranslatedText";
import UITranslations from "../../../../store/uiTranslations";
import Grid from "@material-ui/core/Grid";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Hidden from "@material-ui/core/Hidden";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import makeStyles from "@material-ui/core/styles/makeStyles";
import StarsIcon from "@material-ui/icons/Stars";
import NodeDropdown from "../../../ui/NodeDropdown/NodeDropdown";
import { analytics } from "../../../..";
import { AnalyticsEvents } from "../../../../store/AnalyticsStore";
import {observer} from 'mobx-react'
import { useDistanceMatrix } from "../../../../store/DistanceMatrixStore";

interface IControlsLeft {
  building?: IBuilding;
  queryParams: IQueryParams;
  buildingLangs: ILanguage[];
  translateText: (id: number) => string;
  multiBuildings?: IBuilding[];
  setQueryParamItem: (key: keyof IQueryParams, value: string) => void;
  onBuildingParamsChanged: (queryBuildingId: string) => void;
}

const useStyles = makeStyles(theme => ({
  label: { padding: "8px 10px" },
  autocomplete: {
    width: "100%",
    padding: "0px 10px"
  },
  autocomplete__itemIcon: {
    marginRight: "10px"
  },
  switch__label: {
    marginBottom: "-8px",
  }
}));

const ControlsLeft: FunctionComponent<IControlsLeft> = observer(props => {
  const {
    building,
    queryParams,
    buildingLangs,
    translateText,
    multiBuildings,
    setQueryParamItem,
    onBuildingParamsChanged
  } = props;

  const { buildingId, nodeFrom, nodeTo, routeLang, WheelChair, fixStart } = queryParams;
  const ui = UITranslations.PhotoRoutePage.ControlsPanel.LeftSide;
  const classes = useStyles();
  const { lookupDistance } = useDistanceMatrix();

  const onSelectChange = (k: any, v: string) => {
    if (k === QueryParams.buildingId) {
      onBuildingParamsChanged(v);
    } else {
      setQueryParamItem(k, v);
    }
  };

  const onWheelChange = (checked: boolean) => {
    analytics.logEvent(AnalyticsEvents.ToggleWheelchair, {
      previousValue: !checked,
      value: checked
    });
    const WheelChair = checked ? "true" : "";
    setQueryParamItem(QueryParams.WheelChair, WheelChair);
  };

  const switchery = (
    <SwitchControl
      checked={!!WheelChair}
      onChange={onWheelChange}
      label={<i className="fa fa-wheelchair fa-lg" />}
      classes={{root: classes.switch__label}} 
    />
  );

  const isEmbeded = useMemo(() => window.self !== window.top, [])

  const handleNodeChange = (key: string) => (value: string) => {
    const { queryParams } = queryParamsStore;
    const omitChannelParam = !isEmbeded && queryParams.nodeFrom !== undefined && queryParams.nodeTo !== undefined;
    queryParamsStore.setQueryParam({
      ..._.omit(queryParams, omitChannelParam ? ["channel"] : []),
      [key]: value
    });
  };

  const renderToNodeSelect = () => {
    return (
      <>
        <Grid item container xs={2} classes={{ root: classes.label }}>
          <TranslatedText source={ui.ToLabel} />
          {":"}
        </Grid>
        <Grid item container xs={10}>
          <NodeDropdown
            lookupDistance={lookupDistance}
            type="to-node"
            placeholder={translateText(ui.ToPlaceholder.id)}
            value={nodeTo}
            onChange={v => {
              analytics.logEvent(AnalyticsEvents.ChangeDestinationLocation, {
                previousValue: nodeTo,
                value: v
              });
              handleNodeChange("nodeTo")(v);
            }}
          />
        </Grid>
      </>
    );
  };
  const renderFromNodeSelect = () => {
    return (
      <Grid container>
        <Grid item container xs={2} classes={{ root: classes.label }}>
          <TranslatedText source={ui.FromLabel} />
          {":"}
        </Grid>
        <Grid item container xs={10}>
          <NodeDropdown
            lookupDistance={lookupDistance}
            type="from-node"
            placeholder={translateText(ui.FromPlaceholder.id)}
            value={nodeFrom}
            onChange={v => {
              analytics.logEvent(AnalyticsEvents.ChangeStartLocation, {
                previousValue: nodeFrom,
                value: v
              });
              handleNodeChange("nodeFrom")(v);
            }}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <div className="col-md-6 border-rightside">
      <div className="ml-10 mr-10">
        {multiBuildings && (
          <div className="row destination-control-item mb-20 form-horizontal">
            <SelectControl
              value={buildingId}
              name="buildingId"
              onChange={onSelectChange}
              className="col-xs-12 col-sm-11 col-md-9"
            >
              <BuildingsOptions buildings={multiBuildings} currentBuilding={building} />
            </SelectControl>
          </div>
        )}
        <div className="row destination-control-item mb-20 form-horizontal">{!fixStart && renderFromNodeSelect()}</div>
        <div className="row destination-control-item mb-20 form-horizontal">
          <Grid container>
            {renderToNodeSelect()}
          </Grid>
        </div>
        <div className="row destination-control-item mb-20 form-horizontal">
          <div className="col-xs-2 col-xs-offset-2">
            {switchery}
          </div>
          <div className="col-xs-6 col-xs-offset-2">
            <Select
              fullWidth
              value={routeLang}
              name="routeLang"
              onChange={e => {
                analytics.logEvent(AnalyticsEvents.ChangeLanguage, {
                  previousValue: routeLang,
                  value: e.target.value
                });
                onSelectChange("routeLang", e.target.value as string);
              }}
              placeholder={translateText(PLACEHOLDER_SELECT_START)}
              className=""
            >
              {buildingLangs.map(x => (
                <MenuItem value={x.ietf}>{x.language_name}</MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ControlsLeft;
